import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { useSubscriptionPermissions } from '../hooks/useSubscriptionPermissions';
import { useTrialEligibility } from '../hooks/useTrialEligibility';

interface SubscriptionState {
  plan: string | null;
  status: string | null;
  currentPeriodEnd: string | null;
  isLoading: boolean;
  error: string | null;
  isTrialEligible: boolean;
  trialEligibilityLoading: boolean;
}

interface SubscriptionContextType extends SubscriptionState {
  refreshSubscription: () => Promise<void>;
  openBillingPortal: () => Promise<void>;
  upgradeSubscription: (priceId: string, productId?: string) => Promise<void>;
  cancelSubscription: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const SubscriptionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, session } = useAuth();
  const { subscription, loading: permissionsLoading } = useSubscriptionPermissions();
  const { eligible: isTrialEligible, loading: trialEligibilityLoading } = useTrialEligibility();
  
  const [state, setState] = useState<SubscriptionState>({
    plan: null,
    status: null,
    currentPeriodEnd: null,
    isLoading: true,
    error: null,
    isTrialEligible: true,
    trialEligibilityLoading: true,
  });

  // Update state when subscription data changes
  useEffect(() => {
    if (subscription) {
      setState(prev => ({
        ...prev,
        plan: subscription.plan_name || null,
        status: subscription.status || null,
        currentPeriodEnd: subscription.end_date || null,
        isLoading: permissionsLoading,
        isTrialEligible,
        trialEligibilityLoading,
      }));
    } else {
      setState(prev => ({
        ...prev,
        plan: null,
        status: null,
        currentPeriodEnd: null,
        isLoading: permissionsLoading,
        isTrialEligible,
        trialEligibilityLoading,
      }));
    }
  }, [subscription, permissionsLoading, isTrialEligible, trialEligibilityLoading]);

  const refreshSubscription = useCallback(async () => {
    if (!user || !session || !isSupabaseConfigured || !supabase) {
      return;
    }

    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      // Trigger sync with Stripe
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sync-stripe-subscription`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to sync subscription');
      }

      // State will be updated by useSubscriptionPermissions hook
      setState(prev => ({ ...prev, isLoading: false }));
    } catch (error) {
      console.error('Error refreshing subscription:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to refresh subscription',
      }));
    }
  }, [user, session]);

  const openBillingPortal = useCallback(async () => {
    if (!user || !session || !isSupabaseConfigured || !supabase) {
      throw new Error('User not authenticated');
    }

    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      if (!currentSession?.access_token) {
        throw new Error('No access token');
      }

      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-portal-session`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${currentSession.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          return_url: `${window.location.origin}/settings/billing`,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to create portal session');
      }

      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No portal URL returned');
      }
    } catch (error) {
      console.error('Error opening billing portal:', error);
      throw error;
    }
  }, [user, session]);

  const upgradeSubscription = useCallback(async (priceId: string, productId?: string) => {
    if (!user || !session || !isSupabaseConfigured || !supabase) {
      throw new Error('User not authenticated');
    }

    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      if (!currentSession?.access_token) {
        throw new Error('No access token');
      }

      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/upgrade-subscription`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${currentSession.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          price_id: priceId,
          product_id: productId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to upgrade subscription');
      }

      // Refresh subscription after upgrade
      await refreshSubscription();
    } catch (error) {
      console.error('Error upgrading subscription:', error);
      throw error;
    }
  }, [user, session, refreshSubscription]);

  const cancelSubscription = useCallback(async () => {
    if (!user || !session || !isSupabaseConfigured || !supabase) {
      throw new Error('User not authenticated');
    }

    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      if (!currentSession?.access_token) {
        throw new Error('No access token');
      }

      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/cancel-subscription`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${currentSession.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to cancel subscription');
      }

      const data = await response.json();
      
      // Refresh subscription after cancellation
      await refreshSubscription();
      
      return data;
    } catch (error) {
      console.error('Error canceling subscription:', error);
      throw error;
    }
  }, [user, session, refreshSubscription]);

  const value: SubscriptionContextType = {
    ...state,
    refreshSubscription,
    openBillingPortal,
    upgradeSubscription,
    cancelSubscription,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = (): SubscriptionContextType => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

